/**
 * PricingZonesPage — `/settings/pricing-zones`.
 *
 * Full CRUD surface on top of service-pricing for the pricing_zone table:
 *
 *   - GET    /v1/pricing/zones/admin          — list (active + inactive)
 *   - POST   /v1/pricing/zones                — create
 *   - PATCH  /v1/pricing/zones/:id            — update
 *   - DELETE /v1/pricing/zones/:id            — delete (returns 409 when any
 *                                                tariff still references the zone)
 *
 * Mirrors PricingConfigPage layout: page header with "New zone" button,
 * table with per-row Edit + Delete, modal for create / edit.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe2, Pencil, Plus, Trash2, X } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  createPricingZone,
  deletePricingZone,
  listPricingZonesAdmin,
  type CreatePricingZoneInput,
  type PricingZoneAdmin,
  updatePricingZone,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

const ZONES_ADMIN_KEY = ['pricing', 'zones', 'admin'] as const;

/** Empty form template — used when opening "New zone". */
function blankDraft(): CreatePricingZoneInput {
  return {
    code: '',
    name: '',
    countryCode: 'NG',
    active: true,
  };
}

function rowToDraft(z: PricingZoneAdmin): CreatePricingZoneInput {
  return {
    code: z.code,
    name: z.name,
    countryCode: z.countryCode,
    active: z.active,
  };
}

/** Extract the server-side message from an ApiError body, falling back. */
function extractServerMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | null;
    if (body && typeof body.message === 'string' && body.message.length > 0) {
      return body.message;
    }
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function PricingZonesPage(): JSX.Element {
  const qc = useQueryClient();

  const zones = useQuery({
    queryKey: ZONES_ADMIN_KEY,
    queryFn: () => listPricingZonesAdmin(),
    retry: false,
    staleTime: 60_000,
  });

  const denied =
    zones.isError &&
    zones.error instanceof ApiError &&
    (zones.error.status === 401 || zones.error.status === 403);

  const [modal, setModal] = useState<
    | null
    | { mode: 'create'; draft: CreatePricingZoneInput }
    | { mode: 'edit'; id: string; draft: CreatePricingZoneInput }
  >(null);
  const [confirmDelete, setConfirmDelete] = useState<PricingZoneAdmin | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (input: CreatePricingZoneInput) => createPricingZone(input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ZONES_ADMIN_KEY });
      // Also bust the public zones cache used by PricingConfigPage dropdowns.
      await qc.invalidateQueries({ queryKey: ['pricing', 'zones'] });
      setModal(null);
      setModalError(null);
    },
    onError: (e) => setModalError(extractServerMessage(e, 'Create failed')),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; patch: Partial<CreatePricingZoneInput> }) =>
      updatePricingZone(args.id, args.patch),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ZONES_ADMIN_KEY });
      await qc.invalidateQueries({ queryKey: ['pricing', 'zones'] });
      setModal(null);
      setModalError(null);
    },
    onError: (e) => setModalError(extractServerMessage(e, 'Update failed')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deletePricingZone(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ZONES_ADMIN_KEY });
      await qc.invalidateQueries({ queryKey: ['pricing', 'zones'] });
      setConfirmDelete(null);
      setDeleteError(null);
    },
    onError: (e) => {
      // The server returns the 409 body with the "Cannot delete — N tariffs
      // reference this zone…" message verbatim. Surface it untouched.
      setDeleteError(extractServerMessage(e, 'Delete failed'));
    },
  });

  function submitModal(): void {
    if (!modal) return;
    if (modal.mode === 'create') {
      createMut.mutate(modal.draft);
    } else {
      updateMut.mutate({ id: modal.id, patch: modal.draft });
    }
  }

  const rows = zones.data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pricing zones"
        description="Geographic pricing zones used by the tariff matrix (e.g. LAGOS_METRO, SOUTH_WEST)."
        actions={
          <div className="flex items-center gap-2">
            <Globe2 className="w-4 h-4 text-muted" />
            <button
              type="button"
              onClick={() => {
                setModalError(null);
                setModal({ mode: 'create', draft: blankDraft() });
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> New zone
            </button>
          </div>
        }
      />

      {zones.isPending ? (
        <p className="text-sm text-muted">Loading zones…</p>
      ) : denied ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          <p className="font-semibold">Permission required</p>
          <p>
            The token does not carry{' '}
            <code className="font-mono">Settings/managePricingConfig</code>.
          </p>
        </div>
      ) : zones.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          <p className="font-semibold">Pricing service unreachable</p>
          <p>{(zones.error as Error).message}</p>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No pricing zones found.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Code</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Name</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Country</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Active</th>
                <th className="px-4 py-2 text-right text-[10px] uppercase text-muted">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((z) => (
                <tr key={z.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs font-mono">{z.code}</td>
                  <td className="px-4 py-2 text-xs">{z.name}</td>
                  <td className="px-4 py-2 text-xs font-mono">{z.countryCode}</td>
                  <td className="px-4 py-2 text-xs">
                    {z.active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      aria-label={`Edit zone ${z.code}`}
                      onClick={() => {
                        setModalError(null);
                        setModal({ mode: 'edit', id: z.id, draft: rowToDraft(z) });
                      }}
                      className="text-gray-600 hover:bg-gray-100 rounded p-1 mr-1"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete zone ${z.code}`}
                      onClick={() => {
                        setDeleteError(null);
                        setConfirmDelete(z);
                      }}
                      className="text-red-600 hover:bg-red-50 rounded p-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal ? (
        <ZoneModal
          mode={modal.mode}
          draft={modal.draft}
          onChange={(next) => setModal((m) => (m ? { ...m, draft: next } : m))}
          onCancel={() => {
            setModal(null);
            setModalError(null);
          }}
          onSubmit={submitModal}
          error={modalError}
          submitting={createMut.isPending || updateMut.isPending}
        />
      ) : null}

      {confirmDelete ? (
        <ConfirmDeleteModal
          zone={confirmDelete}
          onCancel={() => {
            setConfirmDelete(null);
            setDeleteError(null);
          }}
          onConfirm={() => deleteMut.mutate(confirmDelete.id)}
          submitting={deleteMut.isPending}
          error={deleteError}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

interface ZoneModalProps {
  readonly mode: 'create' | 'edit';
  readonly draft: CreatePricingZoneInput;
  readonly onChange: (next: CreatePricingZoneInput) => void;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
  readonly error: string | null;
  readonly submitting: boolean;
}

function ZoneModal(props: ZoneModalProps): JSX.Element {
  const { draft, onChange } = props;

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <p className="text-base font-bold text-gray-900">
            {props.mode === 'create' ? 'New zone' : 'Edit zone'}
          </p>
          <button
            type="button"
            onClick={props.onCancel}
            className="p-1.5 rounded hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label htmlFor="zone-code" className="text-[10px] font-semibold uppercase text-muted">
              Code
            </label>
            <input
              id="zone-code"
              type="text"
              aria-label="Code"
              value={draft.code}
              disabled={props.mode === 'edit'}
              onChange={(e) => onChange({ ...draft, code: e.target.value.toUpperCase() })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono uppercase disabled:bg-gray-100"
              placeholder="LAGOS_METRO"
            />
            <p className="text-[10px] text-muted mt-1">
              Uppercase A–Z, 0–9, underscores. Cannot be edited after creation.
            </p>
          </div>
          <div className="col-span-2">
            <label htmlFor="zone-name" className="text-[10px] font-semibold uppercase text-muted">
              Name
            </label>
            <input
              id="zone-name"
              type="text"
              aria-label="Name"
              value={draft.name}
              onChange={(e) => onChange({ ...draft, name: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
              placeholder="Lagos Metro"
            />
          </div>
          <div>
            <label
              htmlFor="zone-country"
              className="text-[10px] font-semibold uppercase text-muted"
            >
              Country (ISO-2)
            </label>
            <input
              id="zone-country"
              type="text"
              maxLength={2}
              aria-label="Country"
              value={draft.countryCode ?? ''}
              onChange={(e) =>
                onChange({ ...draft, countryCode: e.target.value.toUpperCase() })
              }
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono uppercase"
              placeholder="NG"
            />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                aria-label="Active"
                checked={draft.active}
                onChange={(e) => onChange({ ...draft, active: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Active</span>
            </label>
          </div>
        </div>
        {props.error ? (
          <div className="px-4 pb-2 text-xs text-red-600" role="alert">
            {props.error}
          </div>
        ) : null}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={props.onCancel}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={props.onSubmit}
            disabled={props.submitting}
            className="px-3 py-1.5 text-sm bg-nipost-green text-white rounded-lg disabled:opacity-60"
          >
            {props.submitting
              ? 'Saving…'
              : props.mode === 'create'
              ? 'Create zone'
              : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ConfirmDeleteModalProps {
  readonly zone: PricingZoneAdmin;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly submitting: boolean;
  readonly error: string | null;
}

function ConfirmDeleteModal(props: ConfirmDeleteModalProps): JSX.Element {
  const z = props.zone;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-md">
        <div className="p-4 border-b border-border">
          <p className="text-base font-bold text-gray-900">Confirm deletion</p>
        </div>
        <div className="p-4 text-sm text-gray-700 space-y-2">
          <p>
            Delete pricing zone <span className="font-mono">{z.code}</span> ({z.name})?
          </p>
          <p className="text-xs text-muted">
            This action is logged via the FinancialAudit trail. If any tariff rows still
            reference this zone the server will refuse the delete — soft-disable the zone
            (set Active off) if you need to retire it without losing tariffs.
          </p>
        </div>
        {props.error ? (
          <div className="px-4 pb-2 text-xs text-red-600" role="alert">
            {props.error}
          </div>
        ) : null}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={props.onCancel}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={props.onConfirm}
            disabled={props.submitting}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg disabled:opacity-60"
          >
            {props.submitting ? 'Deleting…' : 'Delete zone'}
          </button>
        </div>
      </div>
    </div>
  );
}
